import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useHistory } from 'react-router-dom';
import { buttonColorLink, cardColorClass, cardIconColorClass, iconColorClass } from '../../helpers';
import useSearch from '../../hooks/useSearch';
import type { ColorKeys } from '../../types';

export interface CardItemProps {
  title: string;
  tooltip: string;
  subtitle?: string;
  icon: IconNames;
  color: ColorKeys;
  disabled?: boolean;
  itemLink?: string;
  pageLink?: string;
}

const CardItem = ({ title, tooltip, subtitle, icon, color, disabled, itemLink, pageLink }: CardItemProps) => {
  const { setIsOpen } = useSearch();
  const history = useHistory();

  const onNavigate = () => {
    if (!pageLink && disabled) return;
    setIsOpen(false);
    history.push(disabled ? pageLink || '' : itemLink || '');
  };

  const cardColor = cardColorClass(color);
  const cardIconColor = cardIconColorClass(color);
  const iconColor = iconColorClass(color);
  const btnColor = buttonColorLink(color);

  if (disabled) {
    return (
      <section className='flex h-11 flex-col gap-2 rounded-md border border-gray-3'>
        <div className={cn('group flex flex-col rounded-md bg-gray-2 p-1', pageLink && 'hover:shadow-sm')} onClick={onNavigate}>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <Tooltip content={tooltip} asChild>
                <div className={cn('rounded-md border border-gray-4 bg-gray-3 p-2')}>
                  <Icon name={icon} color='gray' className='size-4' />
                </div>
              </Tooltip>
              <div>
                <p className='font-medium text-xs'>{title}</p>
              </div>
            </div>
            {pageLink && (
              <div className='mr-2 items-center gap-2 opacity-0 duration-200 group-hover:opacity-100'>
                <Button variant='text' size='sm' color='default' className={cn('!size-6')} icon={<Icon name='square-arrow-out-up-right' className={cn('!size-3', iconColor)} />} />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn('group flex flex-col rounded-md border p-1 hover:shadow-sm', cardColor)}>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <Tooltip content={tooltip} asChild>
            <div className={cn('rounded-md border p-2', cardIconColor)}>
              <Icon name={icon} className={cn('size-4', iconColor)} />
            </div>
          </Tooltip>
          <div>
            <p className='font-medium text-xs'>{title}</p>
            {subtitle && <p className='text-[10px]'>{subtitle}</p>}
          </div>
        </div>
        <div className='mr-2 items-center gap-2 opacity-0 group-hover:opacity-100' onClick={onNavigate}>
          <Button
            variant='text'
            size='sm'
            color={btnColor as any}
            className={cn('!size-6', iconColor)}
            icon={<Icon name='square-arrow-out-up-right' className={cn('!size-3', iconColor)} />}
          />
        </div>
      </div>
    </section>
  );
};

export default CardItem;
